import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { callAPI } from "../../api/calls";
import axiosInstance from "../../config/axios";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  X,
  Plus,
  Clock,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Wifi,
  Battery,
  Signal,
  GripVertical,
  MinusCircle,
  Minimize2,
  FileText,
  ClipboardEdit,
} from "lucide-react";

interface CallManagerProps {
  phoneNumber?: string;
}

interface CallState {
  isCallActive: boolean;
  status: string;
  duration: string;
  formattedDuration?: string;
  error: string;
}

interface RecentCall {
  phoneNumber: string;
  timestamp: Date;
  duration?: string;
  status: "completed" | "missed" | "failed";
  callSid?: string;
}

interface CountryCode {
  code: string;
  flag: string;
  name: string;
}

interface VisitDetails {
  title: string;
  templateId: number;
}

const countryCodes: CountryCode[] = [
  { code: "+1", flag: "🇺🇸", name: "USA" },
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+44", flag: "🇬🇧", name: "UK" },
  { code: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "+86", flag: "🇨🇳", name: "China" },
  { code: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "+33", flag: "🇫🇷", name: "France" },
];

export const CallManager: React.FC<CallManagerProps> = ({
  phoneNumber: initialPhoneNumber,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const phoneRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const [callState, setCallState] = useState<CallState>({
    isCallActive: false,
    status: "",
    duration: "",
    error: "",
  });
  const [isMuted, setIsMuted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || "");
  const [showDialpad, setShowDialpad] = useState(false);
  const [showRecents, setShowRecents] = useState(true);

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => {
    const cached = localStorage.getItem("selectedCountryCode");
    return cached ? JSON.parse(cached) : countryCodes[0];
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTime, setCurrentTime] = useState<string>('');
  const [batteryLevel, setBatteryLevel] = useState<number>(85);
  const [signalStrength, setSignalStrength] = useState<number>(4);
  const [visibilityWarningShown, setVisibilityWarningShown] = useState(false);
  const [isScribing, setIsScribing] = useState(false);
  const [selectedCallSid, setSelectedCallSid] = useState<string | null>(null);
  const [visitDetails, setVisitDetails] = useState<VisitDetails>({
    title: "",
    templateId: 1,
  });
  const [templates, setTemplates] = useState([
    { id: 1, name: "General Template" },
    { id: 2, name: "Medical Visit" },
    { id: 3, name: "Customer Support" },
  ]);
  const [processingScribe, setProcessingScribe] = useState(false);
  const [scribeError, setScribeError] = useState("");
  const [scribeSuccess, setScribeSuccess] = useState(false);
  const [isScribeHeaderCollapsed, setIsScribeHeaderCollapsed] = useState(false);

  // Create a ref for the phone number input
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const filteredCountries = countryCodes.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery)
  );

  useEffect(() => {
    if (initialPhoneNumber) {
      handleCallButtonClick();
    }
  }, [initialPhoneNumber]);

  useEffect(() => {
    // Handle keyboard input
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keydown events if the phone is expanded
      if (!isExpanded) return;
      
      if (callState.isCallActive) return; // Don't handle keys during active call

      // Handle numeric keys and special characters
      if (/^[0-9*#]$/.test(e.key)) {
        handleKeyPress(e.key);
        e.preventDefault(); // Prevent default browser behavior
      }
      // Handle backspace
      else if (e.key === "Backspace") {
        handleDelete();
        e.preventDefault(); // Prevent default browser behavior
      }
      // Handle Enter key as call button
      else if (e.key === "Enter" && phoneNumber) {
        handleCallButtonClick();
        e.preventDefault(); // Prevent default browser behavior
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [phoneNumber, callState.isCallActive, isExpanded]);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = 0.5;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle audio playback based on call state
  useEffect(() => {
    if (callState.isCallActive && audioRef.current) {
      // Only attempt to play if not already playing
      if (audioRef.current.paused) {
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log("Audio playback error:", error);
            // Don't throw the error, just log it
          });
        }
      }
    } else if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
  }, [callState.isCallActive]);

  // Update time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const formattedHours = hours % 12 || 12; // Convert to 12-hour format
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      setCurrentTime(`${formattedHours}:${formattedMinutes}`);
    };
    
    // Update time immediately
    updateTime();
    
    // Update time every minute
    const intervalId = setInterval(updateTime, 60000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Update time every second during active calls
  useEffect(() => {
    const updateDuration = () => {
      if (callState.isCallActive && callState.duration) {
        // Format the duration from seconds to MM:SS
        const durationSecs = parseInt(callState.duration, 10);
        const minutes = Math.floor(durationSecs / 60);
        const seconds = durationSecs % 60;
        const formattedDuration = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
        setCallState(prev => ({
          ...prev,
          formattedDuration
        }));
      }
    };
    
    // Update immediately
    updateDuration();
    
    // Update every second if call is active
    let intervalId: number | null = null;
    if (callState.isCallActive) {
      intervalId = window.setInterval(updateDuration, 1000);
    }
    
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [callState.isCallActive, callState.duration]);

  const pollCallStatus = async () => {
    try {
      const status = await callAPI.getCallStatus();
      setCallState((prev) => ({
        ...prev,
        status: status.status,
        duration: status.duration,
        error: status.error,
      }));

      if (["queued", "ringing", "in-progress"].includes(status.status)) {
        setTimeout(() => pollCallStatus(), 2000);
      } else if (status.status === "completed") {
        // Call is completed, reset UI state
        setCallState({
          isCallActive: false,
          status: "",
          duration: "",
          error: "",
        });
        setShowDialpad(true);
        setIsMuted(false);
      }
    } catch (error) {
      console.error("Error polling call status:", error);
    }
  };

  const handleCallButtonClick = async () => {
    if (!phoneNumber) return;

    // Check if the phone number already starts with a plus sign (has country code)
    let fullNumber;
    if (phoneNumber.startsWith('+')) {
      // Already has country code, use as is
      fullNumber = phoneNumber;
    } else {
      // No country code, add the selected country code
      const cleanedNumber = phoneNumber.replace(/\D/g, "");
      fullNumber = `${selectedCountry.code}${cleanedNumber}`;
    }

    console.log("Making call with formatted number:", fullNumber);

    setCallState((prev) => ({ ...prev, isCallActive: true }));
    setShowDialpad(false);
    try {
      const response = await callAPI.initiateCall(
        fullNumber.replace(/\D/g, ""),
        selectedCountry.code
      );
      setCallState((prev) => ({
        ...prev,
        isCallActive: true,
        status: response.status,
      }));
      pollCallStatus();
    } catch (error) {
      console.error("Error initiating call:", error);
      setCallState((prev) => ({
        ...prev,
        isCallActive: false,
        error: "Failed to initiate call",
      }));
      setShowDialpad(true);
    }
  };

  const handleEndCall = async () => {
    try {
      // Pause audio before ending call
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
      
      await callAPI.endCall();
      // Immediately update UI state rather than waiting for polling
      setCallState({
        isCallActive: false,
        status: "",
        duration: "",
        error: "",
      });
      setShowDialpad(true);
      setIsMuted(false);
    } catch (error) {
      console.error("Error ending call:", error);
      // Reset state in case of error
      setCallState({
        isCallActive: false,
        status: "",
        duration: "",
        error: "",
      });
      setShowDialpad(true);
      setIsMuted(false);
    }
  };

  const toggleMute = async () => {
    try {
      const newMuteState = !isMuted;
      await callAPI.toggleMute(newMuteState);
      setIsMuted(newMuteState);
    } catch (error) {
      console.error("Error toggling mute:", error);
    }
  };

  const handleKeyPress = (key: string) => {
    if (phoneNumber.length < 15 && /^[0-9*#]$/.test(key)) {
      setPhoneNumber((prev) => prev + key);
      // Make sure the phone stays expanded
      if (!isExpanded) {
        setIsExpanded(true);
      }
    }
  };

  const handleDelete = () => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  };

  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    localStorage.setItem("selectedCountryCode", JSON.stringify(country));
    setShowCountryDropdown(false);
  };

  const formatPhoneNumberWithCountry = (number: string) => {
    if (!number) return "";
    const cleaned = number.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${selectedCountry.code} (${match[1]}) ${match[2]}-${match[3]}`;
    }
    return `${selectedCountry.code} ${cleaned}`;
  };

  const dialpadButtons = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"],
  ];

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return date.toLocaleDateString();
  };

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent) => {
    if (phoneRef.current) {
      const rect = phoneRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag
  const handleDrag = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag as any);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDrag as any);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  // Handle FAB click with direct positioning
  const handleFabClick = () => {
    if (fabRef.current) {
      const fabRect = fabRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Phone dimensions
      const phoneWidth = 300;
      const phoneHeight = 600;
      
      // Position phone at FAB's position (bottom right corner aligned)
      let x = fabRect.right - phoneWidth;
      let y = fabRect.top - phoneHeight;
      
      // Ensure phone stays within viewport
      x = Math.max(0, Math.min(x, windowWidth - phoneWidth));
      y = Math.max(0, Math.min(y, windowHeight - phoneHeight));
      
      setPosition({ x, y });
    }
    setIsExpanded(true);
    
    // Set dialpad as the default view
    setShowDialpad(true);
    setShowRecents(false);
    
    // Focus on the input field after a short delay to allow the component to render
    setTimeout(() => {
      if (phoneInputRef.current) {
        phoneInputRef.current.focus();
      }
    }, 100);
    
    // Debug log
    console.log("Opening phone with dialpad view");
  };

  // Render battery icon based on level
  const renderBatteryIcon = () => {
    return (
      <div className="relative w-5 h-3 border border-gray-800 rounded-sm">
        <div 
          className="absolute top-0 left-0 h-full bg-gray-800 rounded-sm"
          style={{ width: `${batteryLevel}%` }}
        ></div>
        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-1.5 bg-gray-800 rounded-r-sm"></div>
      </div>
    );
  };

  // Render signal strength bars
  const renderSignalBars = () => {
    return (
      <div className="flex items-end h-3 space-x-0.5">
        {[1, 2, 3, 4].map((bar) => (
          <div 
            key={bar}
            className={`w-0.5 bg-gray-800 rounded-t-sm ${
              bar <= signalStrength ? 'opacity-100' : 'opacity-30'
            }`}
            style={{ height: `${bar * 3}px` }}
          ></div>
        ))}
      </div>
    );
  };

  // Add event listener for beforeunload to warn users if call is active
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (callState.isCallActive) {
        const message = "You are currently on a call. Are you sure you want to leave?";
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [callState.isCallActive]);

  // Add visibility change detection for tab switching
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (callState.isCallActive && document.hidden && !visibilityWarningShown) {
        // Create and show a notification when user switches tabs during call
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("Call in progress", {
            body: "You're still on an active call. Please return to the tab.",
            icon: "/phone-icon.png" // Use an appropriate icon path
          });
        }
        
        // Play alert sound
        const alertSound = new Audio();
        alertSound.src = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBhxQo+z/xHQrCRRAlN//2o9FHixJgL/q/uCmWjFEa5697fbqvHNOVnmUvNfn7tCXdGRviKDO3OPowqhpZXOMob3Q3N3auKF1eYaMoLTIztXWyLOqoZuepens+v775bKRlrvd+f37372HcYu/8v/88K+JdXOHoNL5//fGo4x8aIrB/f/82byabmFwltv//86mjIVtdpfT//nZs4+Bd2h8uP///tSti4NecrX///7fqoVwandTWJeWx9bw//jQn3llS1K49/7/+tGhh21aYqHu///z16:VdLU5an2+/314LCShnFkdKfp/vH54biagnlwYX+z3NnOzMre7/npxbOsonZlb4utydDO1dbn8PTz49rEnpCFcHOGnbK+w8nT3+31+fz8+e7j1MWzoJF/c3SDjJOhrbTB0dvi6/Lz+PrZysmtp5aLfndtZmeGqsfQ19zm7e/w7c+7ui9MOx8qPTcZBhgpLiMXKks4PTNLZ1xiZ3R8b2s1IkNZbJKlhEwgJUVpiqRdJQ0hSH+5wFAH8ula4J54Nwk2Y5O3r1EU8mNa17JfBgo5h+3faQkRM2Swzn0k7FSJ885sBAs5gvX2SAACROTeTwIIM5yxgjj6KXn53E0DCD5/3rgz9SpaluV3GvBAYcnUh0MOMThdts2XTR0SLE6gz6FQBRUwR4HI1H8lBjBCY:bHbriDIVMUFZis/jmmAaNBsSJG3Y52xC5z88EQI1n/LDVuErNiYWM5LS4F/xK0AdIFvX5mQKAkp+t8GJgA==";
        alertSound.play().catch(error => console.log("Error playing alert sound:", error));
        
        setVisibilityWarningShown(true);
      } else if (!document.hidden) {
        setVisibilityWarningShown(false);
      }
    };

    // Request notification permission
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [callState.isCallActive, visibilityWarningShown]);

  // Create portal container when component mounts
  useEffect(() => {
    // Create container for phone portal if it doesn't exist
    let portalContainer = document.getElementById('phone-portal-container');
    
    if (!portalContainer) {
      portalContainer = document.createElement('div');
      portalContainer.id = 'phone-portal-container';
      portalContainer.style.position = 'fixed';
      portalContainer.style.top = '0';
      portalContainer.style.left = '0';
      portalContainer.style.width = '100%';
      portalContainer.style.height = '100%';
      portalContainer.style.pointerEvents = 'none';
      portalContainer.style.zIndex = '9999';
      document.body.appendChild(portalContainer);
    }
    
    // Clean up on unmount
    return () => {
      // Only remove the container if this is the last CallManager
      if (document.querySelectorAll('[data-component="call-manager"]').length <= 1) {
        portalContainer.remove();
      }
    };
  }, []);

  // Function to toggle between dialpad and recent calls
  const toggleRecents = () => {
    setShowRecents(!showRecents);
    setShowDialpad(!showDialpad);
  };

  // Function to handle scribe button click
  const handleScribeClick = (callSid: string) => {
    setSelectedCallSid(callSid);
    setIsScribing(true);
    setVisitDetails({
      title: `Call Recording ${new Date().toLocaleString()}`,
      templateId: 1,
    });
  };

  // Function to close scribe modal
  const closeScribeModal = () => {
    setIsScribing(false);
    setSelectedCallSid(null);
    setVisitDetails({ title: "", templateId: 1 });
    setScribeError("");
    setScribeSuccess(false);
    setProcessingScribe(false);
    setIsScribeHeaderCollapsed(false);
  };

  // Function to process scribe
  const processScribe = async () => {
    if (!selectedCallSid || !visitDetails.title) {
      setScribeError("Please fill in all required fields");
      return;
    }

    setProcessingScribe(true);
    setScribeError("");

    try {
      // Step 1: Get call recording details
      const response = await axiosInstance.get(`/api/calls/${selectedCallSid}`);
      const callData = response.data;

      // Step 2: Process the recording URL
      await axiosInstance.post('/api/scribing/speech/process-url', {
        url: callData.recording_url,
        title: visitDetails.title,
        template_id: visitDetails.templateId
      });

      setScribeSuccess(true);
      
      // After success, redirect to the visit page
      setTimeout(() => {
        window.open(`${window.location.origin}/mynotes`, '_blank');
        closeScribeModal();
      }, 2000);
      
    } catch (error) {
      console.error('Error processing scribe:', error);
      setScribeError(error.message || 'An error occurred while processing the recording');
    } finally {
      setProcessingScribe(false);
    }
  };

  // Render recent calls list with VERY prominent buttons
 

  // Render scribe modal
  const renderScribeModal = () => {
    if (!isScribing) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ pointerEvents: 'auto' }}>
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
          <div
            className="p-4 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsScribeHeaderCollapsed(!isScribeHeaderCollapsed)}
          >
            <h2 className="text-lg font-medium text-gray-800">Create Scribe</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsScribeHeaderCollapsed(!isScribeHeaderCollapsed);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title={isScribeHeaderCollapsed ? "Expand" : "Collapse"}
              >
                {isScribeHeaderCollapsed ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronUp className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeScribeModal();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!isScribeHeaderCollapsed && (
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {scribeSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-4">
                    <FileText className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-1">Processing Complete!</h3>
                  <p className="text-gray-500">Redirecting to visit page...</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={visitDetails.title}
                      onChange={(e) => setVisitDetails({...visitDetails, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter a title for this recording"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                    </label>
                    <select
                      value={visitDetails.templateId}
                      onChange={(e) => setVisitDetails({...visitDetails, templateId: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {scribeError && (
                    <div className="text-red-500 text-sm">
                      {scribeError}
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      onClick={processScribe}
                      disabled={processingScribe}
                      className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                        processingScribe ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                      } transition-colors`}
                    >
                      {processingScribe ? "Processing..." : "Process Recording"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>,
      document.getElementById('phone-portal-container')!
    );
  };

  // Render phone content
  const renderPhoneContent = () => {
    return (
      <>
        {/* Full-screen overlay when call is active - moved to portal for visibility */}
        {callState.isCallActive && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <p className="text-xl font-medium mb-2">Call in progress</p>
              <p className="text-sm text-gray-600 mb-4">Please don't navigate away during an active call</p>
            </div>
          </div>
        )}
        
        <div 
          ref={phoneRef}
          data-component="call-manager"
          className="w-[300px] h-[600px] bg-white rounded-[45px] shadow-2xl overflow-visible border-[8px] border-black relative flex flex-col font-sans"
          style={{ 
            position: 'fixed',
            left: `${position.x}px`,
            top: `${position.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            zIndex: 9999,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
            padding: '10px',
            pointerEvents: 'auto'
          }}
          onMouseDown={handleDragStart}
        >
          {/* Collapse Button - only show when not in a call */}
          {!callState.isCallActive && (
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute -top-5 -right-5 w-10 h-10 bg-red-500 text-white rounded-full shadow-md flex items-center justify-center z-30 hover:bg-red-600 transition-colors border-2 border-white"
              title="Collapse Phone"
            >
              <Minimize2 className="w-5 h-5" />
            </button>
          )}
          
          {/* Status Bar */}
          <div className="h-12 flex items-center justify-between px-6 text-gray-800 text-xs pt-3 bg-gray-50">
            <div className="flex items-center">
              <span className="font-medium tracking-wide">{currentTime}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-end h-3 space-x-[1px]">
                {[1, 2, 3, 4].map((bar) => (
                  <div 
                    key={bar}
                    className={`w-[2px] ${
                      bar <= signalStrength ? 'bg-gray-800' : 'bg-gray-300'
                    } rounded-t-sm`}
                    style={{ height: `${bar * 2 + 2}px` }}
                  ></div>
                ))}
              </div>
              
              <svg viewBox="0 0 15 12" className="w-4 h-3 fill-current text-gray-800">
                <path d="M7.5 0C4.93 0 2.68 1.58 1.88 3.88 0.99 6.43 2.29 9.22 4.7 10.38 5.05 10.56 5.5 10.28 5.5 9.89L5.5 8.38C5.5 8.16 5.29 8 5.11 8 4.22 8 3.5 7.28 3.5 6.38 3.5 5.21 4.86 4.3 6.25 4.88 7.18 5.27 7.5 6.26 7.5 7.19L7.5 9.89C7.5 10.29 7.95 10.56 8.3 10.38 10.7 9.22 12 6.43 11.12 3.88 10.32 1.58 8.08 0 5.5 0L7.5 0Z" />
              </svg>
              
              <div className="relative w-6 h-3">
                <div className="absolute inset-0 border border-gray-800 rounded-sm flex items-center px-[1px]">
                  <div 
                    className="h-[7px] bg-gray-800 rounded-[1px]"
                    style={{ width: `${batteryLevel}%` }}
                  ></div>
                </div>
                <div className="absolute -right-[1px] top-1/2 -translate-y-1/2 w-[1px] h-[5px] bg-gray-800"></div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-white">
            {callState.isCallActive ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-8">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl font-semibold text-blue-600 font-sans">
                    {phoneNumber[0]?.toUpperCase() || "C"}
                  </span>
                </div>
                <div className="space-y-2 text-center">
                  <p className="text-2xl font-semibold text-gray-800 tracking-tight">
                    {formatPhoneNumberWithCountry(phoneNumber)}
                  </p>
                  <p className="text-lg text-gray-500 font-light">
                    {callState.status === "in-progress"
                      ? callState.formattedDuration || callState.duration
                      : callState.status}
                  </p>
                </div>
                <div className="flex items-center justify-center space-x-8 mt-8">
                  <button
                    onClick={toggleMute}
                    className={`p-4 rounded-full transition-colors ${
                      isMuted
                        ? "bg-red-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {isMuted ? (
                      <MicOff className="w-8 h-8" />
                    ) : (
                      <Mic className="w-8 h-8" />
                    )}
                  </button>
                  <button
                    onClick={handleEndCall}
                    className="p-5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <PhoneOff className="w-8 h-8" />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 text-center relative mb-2">
                  <div className="relative">
                    <div
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                    >
                      <span className="text-base">{selectedCountry.flag}</span>
                      <span className="text-sm text-gray-500 font-light">
                        {selectedCountry.code}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </div>
                    <input
                      type="text"
                      ref={phoneInputRef}
                      value={phoneNumber}
                      onChange={(e) => {
                        // Only allow numbers, *, # and + at the start
                        const value = e.target.value;
                        const isValid = /^(\+)?[0-9*#]*$/.test(value);
                        if (isValid && value.length <= 15) {
                          setPhoneNumber(value);
                        }
                      }}
                      className="w-full text-xl text-center font-medium bg-transparent outline-none pl-24 text-gray-800 font-sans tracking-wide truncate"
                      placeholder="Enter number"
                    />
                    {showCountryDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search country..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {filteredCountries.map((country) => (
                            <div
                              key={country.code}
                              onClick={() => {
                                handleCountrySelect(country);
                                setSearchQuery("");
                              }}
                              className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
                            >
                              <span className="text-xl">{country.flag}</span>
                              <span className="flex-1 text-gray-800 font-light">{country.name}</span>
                              <span className="text-gray-500">{country.code}</span>
                            </div>
                          ))}
                          {filteredCountries.length === 0 && (
                            <div className="p-2 text-center text-gray-500">
                              No countries found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

               

            
                  <div className="p-4 bg-gray-50 flex-1">
                    <div className="grid grid-cols-3 gap-4 h-full">
                      {dialpadButtons.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          {row.map((button) => (
                            <button
                              key={button}
                              onClick={() => handleKeyPress(button)}
                              className="aspect-square rounded-full bg-white text-xl font-medium text-gray-800
                                hover:bg-gray-100 active:bg-gray-200 transition-colors
                                shadow-sm flex items-center justify-center font-sans tracking-wide"
                            >
                              {button}
                            </button>
                          ))}
                        </React.Fragment>
                      ))}
                      <button
                        onClick={handleDelete}
                        className="aspect-square rounded-full bg-white text-gray-800
                        hover:bg-gray-100 active:bg-gray-200 transition-colors
                        shadow-sm flex items-center justify-center"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M9 15L15 9M15 15L9 9"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={handleCallButtonClick}
                        disabled={!phoneNumber}
                        className={`aspect-square rounded-full ${
                          phoneNumber
                            ? "bg-green-500 hover:bg-green-600 active:bg-green-700"
                            : "bg-gray-300"
                        } text-white
                        transition-colors shadow-sm flex items-center justify-center`}
                      >
                        <Phone className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                
              </>
            )}
          </div>
          
          {/* Home Indicator - Hidden close functionality */}
          <div className="h-8 flex items-center justify-center bg-white">
            <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
          </div>
        </div>
        
        {/* Scribe Modal */}
        {renderScribeModal()}
      </>
    );
  };

  return (
    <div className="fixed bottom-4 right-24">
      {!isExpanded ? (
        <button
          ref={fabRef}
          onClick={handleFabClick}
          className="w-16 h-16 bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
        >
          <Phone className="w-7 h-7" />
        </button>
      ) : (
        <>
          {/* Render phone through portal to ensure it's always on top */}
          {isExpanded && document.getElementById('phone-portal-container') && 
            ReactDOM.createPortal(
              renderPhoneContent(),
              document.getElementById('phone-portal-container')!
            )
          }
        </>
      )}
    </div>
  );
};
